// Bundle registry.
//
// Bundles are declared explicitly in `bundles/index.ts` (the BUNDLE_MANIFESTS
// list). Both the browser (Vite) and the server (Node) consume the same
// manifest — JSON via static import, behaviors via lazy `() => import(...)`
// thunks — so there's no per-platform glob to keep in sync.

import type { BehaviorFactory } from './Behavior';
import { validateMap, type MapJSON } from './MapJSON';
import {
  BUNDLE_MANIFESTS,
  type BehaviorLoader,
  type MaterialLoader
} from './bundles/index';

export interface MapBundle {
  id: string;
  json: MapJSON;
  /** Lazy imports for behaviors/materials in this bundle. Keys are the
   *  module names referenced from map.json's `behaviors[].module`. */
  behaviorLoaders: Record<string, BehaviorLoader>;
  materialLoaders: Record<string, MaterialLoader>;
}

const bundles = new Map<string, MapBundle>();

for (const manifest of BUNDLE_MANIFESTS) {
  try {
    validateMap(manifest.json);
  } catch (err) {
    throw new Error(`[maps/registry] bundle ${manifest.dir}: ${(err as Error).message}`);
  }
  const json = manifest.json as MapJSON;
  const id = json.id;
  const bundle: MapBundle = {
    id,
    json,
    behaviorLoaders: manifest.behaviors,
    materialLoaders: manifest.materials
  };
  bundles.set(id, bundle);
  // Pre-refactor consumers may still index by the on-disk directory name —
  // register the bundle under both keys so legacy lookups keep working.
  if (manifest.dir !== id) bundles.set(manifest.dir, bundle);
}

export function getBundle(id: string): MapBundle | undefined {
  return bundles.get(id);
}

export function listBundles(): MapBundle[] {
  // Deduplicate — a bundle indexed under both its dir name and its json id
  // appears twice in the Map.
  const seen = new Set<MapBundle>();
  const out: MapBundle[] = [];
  for (const b of bundles.values()) {
    if (seen.has(b)) continue;
    seen.add(b);
    out.push(b);
  }
  return out;
}

/** Per-bundle behavior factory cache. Populated by `preloadBundle`; read by
 *  `getCachedBehaviorFactories`. Keyed by bundle.id. */
const behaviorFactoryCache = new Map<string, Map<string, BehaviorFactory>>();

/** Preload a bundle: async-imports every behavior module declared in its
 *  map.json and caches the factories. Follows the same pattern as
 *  preloadSpaceshipAssets so main.ts can `await` once and then construct
 *  MapArena synchronously on the game loop's hot path. */
export async function preloadBundle(id: string): Promise<MapBundle | undefined> {
  const bundle = bundles.get(id);
  if (!bundle) return undefined;
  if (behaviorFactoryCache.has(bundle.id)) return bundle;

  const factories = new Map<string, BehaviorFactory>();
  const names = new Set<string>();
  for (const def of bundle.json.behaviors ?? []) names.add(def.module);
  await Promise.all(Array.from(names).map(async (name) => {
    const loader = bundle.behaviorLoaders[name];
    if (!loader) {
      throw new Error(
        `[maps/registry] ${bundle.id}: behavior module "${name}" not found. ` +
        `Expected an entry in src/maps/bundles/index.ts under the bundle's behaviors map.`
      );
    }
    const mod = await loader();
    if (typeof mod.default !== 'function') {
      throw new Error(
        `[maps/registry] ${bundle.id}: behavior "${name}" must default-export a factory function returning a Behavior`
      );
    }
    factories.set(name, mod.default);
  }));
  behaviorFactoryCache.set(bundle.id, factories);
  return bundle;
}

export function getCachedBehaviorFactories(bundleId: string): Map<string, BehaviorFactory> | undefined {
  return behaviorFactoryCache.get(bundleId);
}

/** Sentinel id used for the editor → playtest hand-off. The real bundle on
 *  disk supplies behaviors + materials; only the json gets swapped for the
 *  editor's in-memory snapshot. */
export const TRANSIENT_BUNDLE_ID = '__editor_playtest';

/** Register a transient bundle under TRANSIENT_BUNDLE_ID. Inherits behavior
 *  + material loaders from `baseBundleId`'s registration, which MUST already
 *  be preloaded (we need the cached factories in place so MapArena can
 *  construct synchronously).
 *
 *  Throws if baseBundleId isn't registered. Any existing transient bundle
 *  is replaced. */
export function registerTransientBundle(json: MapJSON, baseBundleId: string): MapBundle {
  const base = bundles.get(baseBundleId);
  if (!base) {
    throw new Error(
      `[maps/registry] transient bundle requires an on-disk base; id "${baseBundleId}" not found`
    );
  }
  const transient: MapBundle = {
    id: TRANSIENT_BUNDLE_ID,
    json: { ...json, id: TRANSIENT_BUNDLE_ID },
    behaviorLoaders: base.behaviorLoaders,
    materialLoaders: base.materialLoaders
  };
  bundles.set(TRANSIENT_BUNDLE_ID, transient);
  // Share the base bundle's preloaded behavior factories — the transient
  // uses the exact same behavior modules.
  const baseFactories = behaviorFactoryCache.get(base.id);
  if (baseFactories) {
    behaviorFactoryCache.set(TRANSIENT_BUNDLE_ID, baseFactories);
  }
  return transient;
}

export function unregisterTransientBundle(): void {
  bundles.delete(TRANSIENT_BUNDLE_ID);
  behaviorFactoryCache.delete(TRANSIENT_BUNDLE_ID);
}
